/**
 * The organisation's logos, and the panel for changing them.
 *
 * WHY TWO. A brand mark is drawn for one kind of background. The VTG wordmark
 * is dark navy on transparent: perfect on white, nearly invisible on the dark
 * theme. Roughly a fifth of its ink is near-white, so it fails in the other
 * direction too. One logo cannot serve both surfaces, so the app holds both and
 * picks by theme.
 *
 * The LIGHT logo is also the one PDF reports will use, whatever theme the person
 * generating the report happens to be in - paper is white either way.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { useTheme } from './theme';
import { BEACON_LIGHT, BEACON_DARK } from './brand';

interface Branding {
  logoLight: string | null;
  logoDark: string | null;
  updatedBy?: string | null;
}

const EMPTY: Branding = { logoLight: null, logoDark: null };

const Ctx = createContext<{ branding: Branding; reload: () => void }>({
  branding: EMPTY,
  reload: () => {},
});

export function BrandingProvider({ orgId, children }: { orgId: string | null; children: React.ReactNode }) {
  const [branding, setBranding] = useState<Branding>(EMPTY);

  const reload = useCallback(() => {
    if (!orgId) { setBranding(EMPTY); return; }
    void (async () => {
      try {
        const r = await httpsCallable(functions, 'getBranding')({ orgId });
        setBranding((r.data as Branding) ?? EMPTY);
      } catch {
        // Branding is decoration. Never let it break the page.
        setBranding(EMPTY);
      }
    })();
  }, [orgId]);

  useEffect(() => { reload(); }, [reload]);

  const value = useMemo(() => ({ branding, reload }), [branding, reload]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * The logo for the CURRENT theme, falling back sensibly.
 *
 * If an org has uploaded only one, that one is used on both surfaces - a
 * slightly wrong logo beats a missing one, and the Branding panel says which
 * slot is empty.
 */
export function useLogo(): string {
  return useLogoState().src;
}

/**
 * The logo to draw, and whether it is the product's rather than the tenant's.
 *
 * THE FALLBACK IS THE FEATURE. A new organisation has no logo, and rather than
 * writing the Backup Beacon artwork into every org document at creation - two
 * kilobytes of SVG duplicated per tenant, and a "remove" that would leave a
 * blank instead of restoring the default - an absent slot simply RESOLVES to
 * Beacon. Creating and removing then behave identically, because they are the
 * same state.
 *
 * UPLOADING ONE SLOT DROPS THE DEFAULT FROM BOTH. If a tenant supplies only a
 * light logo, dark mode uses that light logo rather than falling through to
 * Beacon dark. Their mark on the wrong background is a tenant's own choice to
 * fix; OUR mark appearing beside their mark, depending on the theme, looks like
 * the software cannot decide whose product it is.
 *
 * `isDefault` is returned so callers can avoid drawing Beacon twice - see the
 * header in App.tsx.
 */
export function useLogoState(): { src: string; isDefault: boolean } {
  const { branding } = useContext(Ctx);
  const [theme] = useTheme();
  const dark = theme === 'dark';
  const preferred = dark ? branding.logoDark : branding.logoLight;
  const other = dark ? branding.logoLight : branding.logoDark;
  const own = preferred || other;
  if (own) return { src: own, isDefault: false };
  return { src: dark ? BEACON_DARK : BEACON_LIGHT, isDefault: true };
}

export function useBranding() {
  return useContext(Ctx);
}

/** Read a picked file as a data URI, so it can go straight into Firestore. */
function readAsDataUri(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error('Could not read that file.'));
    r.readAsDataURL(file);
  });
}

const MAX_BYTES = 320_000;

export function BrandingPanel({ orgId, canManage }: { orgId: string | null; canManage: boolean }) {
  const { branding, reload } = useBranding();
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  if (!orgId) return null;

  async function pick(slot: 'logoLight' | 'logoDark', file: File | null) {
    if (!file) return;
    setBusy(slot); setMsg(null);
    try {
      if (file.size > MAX_BYTES) {
        throw new Error(`That file is ${Math.round(file.size / 1024)} KB. Keep logos under about 300 KB.`);
      }
      const uri = await readAsDataUri(file);
      await httpsCallable(functions, 'setBranding')({ orgId, [slot]: uri });
      setMsg({ ok: true, text: 'Saved.' });
      reload();
    } catch (err) {
      setMsg({ ok: false, text: err instanceof Error ? err.message : 'That did not save.' });
    } finally { setBusy(null); }
  }

  async function clear(slot: 'logoLight' | 'logoDark') {
    setBusy(slot); setMsg(null);
    try {
      await httpsCallable(functions, 'setBranding')({ orgId, [slot]: null });
      setMsg({ ok: true, text: 'Removed. The Backup Beacon logo is used again.' });
      reload();
    } catch (err) {
      setMsg({ ok: false, text: err instanceof Error ? err.message : 'That did not work.' });
    } finally { setBusy(null); }
  }

  return (
    <section className="panel">
      <h2 className="panel-title">Branding</h2>
      <p className="panel-note">
        Two versions of the same mark, because one cannot sit on both a white page and a dark
        sidebar. Transparent PNG or SVG works best. The light logo is also the one used on PDF
        reports, since paper is white whichever theme you are in.
      </p>

      {msg && <div className={'banner ' + (msg.ok ? 'banner-ok' : 'banner-error')}>{msg.text}</div>}

      <div className="brand-slots">
        <Slot
          label="Light logo"
          hint="For white backgrounds and PDF reports. Usually the full-colour mark."
          value={branding.logoLight}
          preview="light"
          busy={busy === 'logoLight'}
          canManage={canManage}
          onPick={(f) => void pick('logoLight', f)}
          onClear={() => void clear('logoLight')}
        />
        <Slot
          label="Dark logo"
          hint="For the dark theme. Usually a white or knocked-out version of the same mark."
          value={branding.logoDark}
          preview="dark"
          busy={busy === 'logoDark'}
          canManage={canManage}
          onPick={(f) => void pick('logoDark', f)}
          onClear={() => void clear('logoDark')}
        />
      </div>

      {(!branding.logoLight || !branding.logoDark) && (
        <p className="panel-note">
          {!branding.logoLight && !branding.logoDark
            ? 'Neither slot is set, so the Backup Beacon logo is used - which is the default for every new organisation.'
            : 'Only one slot is set, so it is used on BOTH surfaces - the Backup Beacon default stops being used the moment you upload anything. Upload the other and each surface gets the right one.'}
        </p>
      )}
    </section>
  );
}

function Slot({
  label, hint, value, preview, busy, canManage, onPick, onClear,
}: {
  label: string;
  hint: string;
  value: string | null;
  /** Which background to preview against - the whole point of having two. */
  preview: 'light' | 'dark';
  busy: boolean;
  canManage: boolean;
  onPick: (f: File | null) => void;
  onClear: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <div className="brand-slot">
      <div className="brand-slot-hd">
        <span className="brand-slot-name">{label}</span>
        {value ? <span className="tag tag-ok">set</span> : <span className="tag">not set</span>}
      </div>
      <div className="panel-note" style={{ marginTop: 0 }}>{hint}</div>

      {/* Previewed on the background it is FOR. A dark logo shown on white looks
          broken and vice versa, which is exactly the mistake this prevents. */}
      <div className={'brand-preview brand-preview-' + preview}>
        {value
          ? <img src={value} alt={label} />
          : <span className="dim" style={{ fontSize: 11 }}>Nothing uploaded</span>}
      </div>

      {canManage && (
        <div className="row-form">
          <input
            ref={input} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml"
            style={{ display: 'none' }}
            onChange={(e) => { onPick(e.target.files?.[0] ?? null); e.target.value = ''; }}
          />
          <button className="btn" disabled={busy} onClick={() => input.current?.click()}>
            {busy ? 'Saving...' : value ? 'Replace' : 'Upload'}
          </button>
          {value && (
            <button className="btn-link danger" disabled={busy} onClick={onClear}>Remove</button>
          )}
        </div>
      )}
    </div>
  );
}
